import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from 'framer-motion';
import { FREE_PLAN_PRICE, PAYWALL_PLANS } from '../config/pricingPublic';
import './landing.css';

const LANDING_OWNER_OPERATOR = PAYWALL_PLANS.find((p) => p.tier === 'owner-operator');
const LANDING_FLEET = PAYWALL_PLANS.find((p) => p.tier === 'fleet');

function useIsMobile768() {
  const [m, setM] = useState(false);
  useEffect(() => {
    const q = window.matchMedia('(max-width: 768px)');
    const fn = () => setM(q.matches);
    fn();
    q.addEventListener('change', fn);
    return () => q.removeEventListener('change', fn);
  }, []);
  return m;
}

function springTransition(reduce) {
  return reduce ? { duration: 0 } : { type: 'spring', stiffness: 90, damping: 22 };
}

function fadeUpProps(delay, reduce) {
  return {
    initial: reduce ? false : { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: reduce ? { duration: 0 } : { ...springTransition(reduce), delay },
  };
}

function ChartIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <rect x="2" y="10" width="3" height="4" rx="1" fill="#E09B1A" />
      <rect x="6.5" y="6" width="3" height="8" rx="1" fill="#E09B1A" />
      <rect x="11" y="3" width="3" height="11" rx="1" fill="#E09B1A" opacity="0.45" />
    </svg>
  );
}

function HeroDashboardMockup({ rotateXMotion, reduceMotion, isMobile }) {
  const flat = reduceMotion || isMobile;

  return (
    <div className="landing-hero-dash-persp">
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={
          reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 70, damping: 18, delay: 0.5 }
        }
      >
        <motion.div
          className="landing-hero-dashboard-mockup"
          style={{
            rotateX: flat ? 0 : rotateXMotion,
            transformPerspective: 1200,
            transformStyle: 'preserve-3d',
          }}
        >
        <div className="landing-dash-chrome">
          <div className="landing-dash-dots" aria-hidden>
            <span className="landing-dash-dot landing-dash-dot--r" />
            <span className="landing-dash-dot landing-dash-dot--y" />
            <span className="landing-dash-dot landing-dash-dot--g" />
          </div>
          <div className="landing-dash-url">
            <div className="landing-dash-url-inner">sovrynn.co/dashboard</div>
          </div>
        </div>
        <div className="landing-dash-body">
          <aside className="landing-dash-sidebar">
            <div className="landing-dash-sidebar__mark">Sovrynn</div>
            <div className="landing-dash-nav-fake" aria-hidden>
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
            </div>
            <div className="landing-dash-avatar" aria-hidden />
          </aside>
          <div className="landing-dash-main">
            <div className="landing-dash-greet">
              <strong>Hello, Tearra</strong>
              <span>Your compliance intelligence, at a glance.</span>
            </div>
            <div className="landing-dash-dri-card">
              <div className="landing-dash-dri-card__label">
                <ChartIcon />
                DRI Score™
              </div>
              <div className="landing-dash-dri-card__score-row">
                <span className="landing-dash-dri-card__score">78</span>
                <span className="landing-dash-dri-card__max">/100</span>
              </div>
              <span className="landing-dash-badge">Compliance Verified</span>
              <div className="landing-dash-bar">
                <div className="landing-dash-bar__fill" style={{ width: '78%' }} />
              </div>
              <div className="landing-dash-bar">
                <div className="landing-dash-bar__fill landing-dash-bar__fill--dim" style={{ width: '60%' }} />
              </div>
            </div>
            <div className="landing-dash-pillars">
              <div className="landing-dash-pillar">
                <strong>Document Health</strong>
                <span>78%</span>
              </div>
              <div className="landing-dash-pillar">
                <strong>Vehicle Readiness™</strong>
                <span>85/100</span>
              </div>
              <div className="landing-dash-pillar">
                <strong>Authority Health</strong>
                <span>Current</span>
              </div>
            </div>
          </div>
        </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default function LandingPage() {
  const reduceMotion = useReducedMotion();
  const isMobile = useIsMobile768();
  const { scrollY } = useScroll();
  const dashboardRotateX = useTransform(scrollY, [0, 300], [4, 0]);
  const [navScrolled, setNavScrolled] = useState(false);

  const onScroll = useCallback(() => {
    setNavScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  const scrollToFeatures = () => {
    const el = document.getElementById('features');
    if (el) {
      el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  };

  const viewAnim = {
    once: true,
    margin: '-80px',
    amount: 0.25,
  };

  const staggerFast = reduceMotion ? 0 : 0.12;

  return (
    <div className="landing">
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <nav className={`landing-nav${navScrolled ? ' landing-nav--scrolled' : ''}`} aria-label="Primary">
        <Link to="/" className="landing-nav__brand">
          <span className="landing-nav__wordmark">Sovrynn</span>
          <span className="landing-nav__trails" aria-hidden>
            <span className="landing-nav__trail landing-nav__trail--1" />
            <span className="landing-nav__trail landing-nav__trail--2" />
          </span>
        </Link>
        <div className="landing-nav__actions">
          <Link to="/login" className="landing-nav__signin">
            Sign In
          </Link>
          <Link to="/register" className="landing-nav__cta">
            Get Started Free
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="landing-hero" aria-label="Hero">
        <div className="landing-hero__bg-grid" aria-hidden />
        <div className="landing-hero__glow" aria-hidden />
        <div
          className="landing-hero__sweep-line landing-hero__sweep-line--1"
          aria-hidden
          style={{ '--line-opacity': 0.15 }}
        />
        <div
          className="landing-hero__sweep-line landing-hero__sweep-line--2"
          aria-hidden
          style={{ '--line-opacity': 0.08 }}
        />
        <div
          className="landing-hero__sweep-line landing-hero__sweep-line--3"
          aria-hidden
          style={{ '--line-opacity': 0.05 }}
        />

        <div className="landing-hero__inner">
          <motion.p className="landing-hero__eyebrow" {...fadeUpProps(0, reduceMotion)}>
            {'COMPLIANCE INTELLIGENCE PLATFORM\nFOR INDEPENDENT OPERATORS'}
          </motion.p>
          <motion.h1 className="landing-hero__headline" {...fadeUpProps(0.1, reduceMotion)}>
            <span className="landing-hero__headline-line landing-hero__headline-line--white">Your operation.</span>
            <span className="landing-hero__headline-line landing-hero__headline-line--amber">Your intelligence.</span>
          </motion.h1>

          <motion.div
            className="landing-hero__head-trails"
            aria-hidden
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: reduceMotion ? 0 : 0.4, duration: reduceMotion ? 0 : 0.8 }}
          >
            <motion.div
              className="lp-trail"
              style={{ width: 180, maxWidth: '100%' }}
              initial={reduceMotion ? false : { width: 0 }}
              animate={{ width: 180 }}
              transition={{ delay: reduceMotion ? 0 : 0.4, duration: reduceMotion ? 0 : 0.8, ease: 'easeOut' }}
            />
            <motion.div
              className="lp-trail lp-trail--dim"
              style={{ width: 120, maxWidth: '100%' }}
              initial={reduceMotion ? false : { width: 0 }}
              animate={{ width: 120 }}
              transition={{ delay: reduceMotion ? 0 : 0.48, duration: reduceMotion ? 0 : 0.8, ease: 'easeOut' }}
            />
          </motion.div>

          <motion.p className="landing-hero__sub" {...fadeUpProps(0.2, reduceMotion)}>
            The independent commercial operator carries the full compliance burden with none of the infrastructure.
            Sovrynn is the Compliance Intelligence platform that gives you real-time visibility, a DRI Score™ that shows
            exactly where you stand, and a Compliance Passport™ that makes your verified record impossible to ignore.
          </motion.p>
          <motion.p className="landing-hero__fine" {...fadeUpProps(0.25, reduceMotion)}>
            Free forever. No credit card required.
          </motion.p>
          <motion.div className="landing-hero__ctas" {...fadeUpProps(0.3, reduceMotion)}>
            <Link to="/register" className="landing-btn-primary">
              Get Your Free DRI Score™
            </Link>
            <button type="button" className="landing-btn-secondary" onClick={scrollToFeatures}>
              See how it works
            </button>
          </motion.div>

          <HeroDashboardMockup
            rotateXMotion={dashboardRotateX}
            reduceMotion={reduceMotion}
            isMobile={isMobile}
          />
        </div>
      </section>

      {/* Social proof */}
      <section className="landing-social" aria-label="Trust">
        <div className="landing-social__inner">
          <p className="landing-social__text">Trusted by independent commercial operators across the US</p>
          <div className="landing-social__trails" aria-hidden>
            <span className="landing-social__trail landing-social__trail--1" />
            <span className="landing-social__trail landing-social__trail--2" />
            <span className="landing-social__trail landing-social__trail--3" />
          </div>
          <p className="landing-social__text landing-social__text--right">Built for OTR owner-operators, local/regional operators, new authority holders, and small fleets</p>
        </div>
      </section>

      {/* Problem */}
      <section className="landing-problem" aria-labelledby="lp-problem-h">
        <div className="landing-problem__inner">
          <p className="landing-section-label">THE REALITY</p>
          <h2 id="lp-problem-h" className="landing-problem__title">
            <span className="landing-problem__title-line landing-problem__title-line--white">3.5 million drivers.</span>
            <span className="landing-problem__title-line landing-problem__title-line--amber">Zero good tools.</span>
          </h2>
          <div className="landing-problem__trails" aria-hidden>
            <div className="lp-trail landing-problem__trail--1" />
            <div className="lp-trail lp-trail--dim landing-problem__trail--2" />
          </div>
          <div className="landing-problem__cards">
            {[
              {
                num: '01',
                title: 'Compliance is a second job',
                body:
                  'Owner-operators have no safety department. You are the driver, the dispatcher, and the compliance manager — all at once.',
              },
              {
                num: '02',
                title: 'Enterprise tools ignore you',
                body:
                  'Every major Compliance Intelligence platform is built for large fleets with safety managers. The 1–25 truck market gets expensive, bloated software that was never designed for how you operate.',
              },
              {
                num: '03',
                title: 'You need clarity before surprises',
                body:
                  'Independent operators move fast, and compliance gaps compound quietly. You need real-time visibility into what is current, what is expiring, and what needs action next.',
              },
            ].map((c, i) => (
              <motion.article
                key={c.num}
                className="landing-problem-card"
                initial={reduceMotion ? false : { opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewAnim}
                transition={{ ...springTransition(reduceMotion), delay: reduceMotion ? 0 : i * staggerFast }}
              >
                <div className="landing-problem-card__num">{c.num}</div>
                <h3 className="landing-problem-card__title">{c.title}</h3>
                <p className="landing-problem-card__body">{c.body}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <div className="landing-features" id="features">
        {/* Row 1 — DRI */}
        <section className="landing-feature-row landing-feature-row--dark" aria-labelledby="lp-f1">
          <div className="landing-feature-row__inner">
            <div>
              <p className="landing-section-label">DRI SCORE™</p>
              <h2 id="lp-f1" className="landing-feature-row__title">
                Your compliance number.
              </h2>
              <p className="landing-feature-row__body">
                Your compliance standing, scored in real time from 0 to 100. The number that tells you exactly where you
                are before the DOT, a broker, or an inspector does.
              </p>
              <ul className="landing-feature-list">
                <li>Document completeness</li>
                <li>Vehicle readiness</li>
                <li>Authority health</li>
                <li>Expiration visibility detection</li>
              </ul>
            </div>
            <motion.div
              className="landing-feature-row__visual"
              initial={reduceMotion ? false : { opacity: 0, x: 48 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={viewAnim}
              transition={springTransition(reduceMotion)}
            >
              <div className="landing-mock-dri">
                <div className="landing-mock-dri__label">
                  <ChartIcon />
                  DRI Score™
                </div>
                <div className="landing-mock-dri__score">
                  <span className="landing-mock-dri__score-num">78</span>
                  <span className="landing-mock-dri__score-suffix">/100</span>
                </div>
                <span className="landing-dash-badge">Compliance Verified</span>
                <div className="landing-mock-dri__rows">
                  <div className="landing-mock-dri__row">
                    <span className="landing-mock-dri__row-label">Document Health</span>
                    <div className="landing-mock-dri__row-bar-wrap">
                      <div className="landing-mock-dri__row-bar" style={{ width: '78%' }} />
                    </div>
                    <span className="landing-mock-dri__row-val">78%</span>
                  </div>
                  <div className="landing-mock-dri__row">
                    <span className="landing-mock-dri__row-label">Vehicle Readiness™</span>
                    <div className="landing-mock-dri__row-bar-wrap">
                      <div className="landing-mock-dri__row-bar" style={{ width: '85%' }} />
                    </div>
                    <span className="landing-mock-dri__row-val">85%</span>
                  </div>
                  <div className="landing-mock-dri__row">
                    <span className="landing-mock-dri__row-label">Authority Health</span>
                    <span className="landing-mock-dri__row-val landing-mock-dri__row-val--green">Current</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Row 2 — Passport */}
        <section className="landing-feature-row landing-feature-row--surface" aria-labelledby="lp-f2">
          <div className="landing-feature-row__inner landing-feature-row__inner--visual-first">
            <motion.div
              className="landing-feature-row__visual"
              initial={reduceMotion ? false : { opacity: 0, x: -48, rotate: reduceMotion ? 0 : -4 }}
              whileInView={{ opacity: 1, x: 0, rotate: reduceMotion ? 0 : -2 }}
              viewport={viewAnim}
              transition={springTransition(reduceMotion)}
            >
              <div className="landing-mock-passport">
                <div className="landing-mock-passport__brand">Sovrynn</div>
                <div className="landing-mock-passport__trails" aria-hidden>
                  <div className="lp-trail" />
                  <div className="lp-trail lp-trail--dim" />
                </div>
                <h3 className="landing-mock-passport__title">Compliance Passport™</h3>
                <p className="landing-mock-passport__sub">Verified Living Compliance Record</p>
                <div className="landing-mock-passport__id-block">
                  <div className="landing-mock-passport__id-label">PASSPORT ID</div>
                  <div className="landing-mock-passport__id-code">SVN-A3K9P2</div>
                  <p className="landing-mock-passport__meta">Generated April 3, 2026</p>
                  <p className="landing-mock-passport__meta">Expires May 3, 2026</p>
                  <span className="landing-mock-passport__link">Verify at sovrynn.co/passport/…</span>
                </div>
                <div className="landing-mock-passport__dri">
                  <div className="landing-mock-passport__dri-label">DRI Score™</div>
                  <div className="landing-mock-passport__dri-score">78 /100</div>
                  <span className="landing-dash-badge">Compliance Verified</span>
                </div>
                <div className="landing-mock-passport__docs">
                  <div className="landing-mock-passport__doc-row" style={{ color: '#22c55e' }}>
                    <span>Medical Examiner&apos;s Certificate — Complete</span>
                  </div>
                  <div className="landing-mock-passport__doc-row" style={{ color: '#22c55e' }}>
                    <span>Commercial Driver&apos;s License — Complete</span>
                  </div>
                  <div className="landing-mock-passport__doc-row landing-mock-passport__doc-row--amber">
                    <span>Motor Vehicle Record — Expiring Soon</span>
                  </div>
                </div>
              </div>
            </motion.div>
            <div>
              <p className="landing-section-label">COMPLIANCE PASSPORT™</p>
              <h2 id="lp-f2" className="landing-feature-row__title">
                Prove it to anyone.
              </h2>
              <p className="landing-feature-row__body">
                A verified, living compliance record with a unique ID (SVN-XXXXXX). Not a "please trust me" document,
                but a record that speaks for itself: scored, timestamped, and impossible to replicate.
              </p>
              <ul className="landing-feature-list">
                <li>Unique passport ID (SVN-XXXXXX)</li>
                <li>Public verification link</li>
                <li>PDF download ready</li>
                <li>Updates automatically</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Row 3 — Rynn */}
        <section className="landing-feature-row landing-feature-row--dark" aria-labelledby="lp-f3">
          <div className="landing-feature-row__inner">
            <div>
              <p className="landing-section-label">RYNN — COMPLIANCE INTELLIGENCE DIRECTOR</p>
              <h2 id="lp-f3" className="landing-feature-row__title">
                Ask anything.
              </h2>
              <p className="landing-feature-row__body">
                The compliance director independent operators never had. Powered by Claude, Rynn knows FMCSA regulations
                inside out and answers in plain language, always available, never billing by the hour.
              </p>
              <ul className="landing-feature-list">
                <li>FMCSA regulation expertise</li>
                <li>Personalized to your compliance status</li>
                <li>Available 24/7 on every page</li>
                <li>Clear answers without consultant overhead</li>
              </ul>
            </div>
            <motion.div
              className="landing-feature-row__visual"
              initial={reduceMotion ? false : { opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={viewAnim}
              transition={springTransition(reduceMotion)}
            >
              <motion.div
                className="landing-mock-chat"
                initial="hidden"
                whileInView="show"
                viewport={viewAnim}
                variants={{
                  hidden: {},
                  show: {
                    transition: { staggerChildren: reduceMotion ? 0 : 0.15, delayChildren: reduceMotion ? 0 : 0.1 },
                  },
                }}
              >
                <div className="landing-mock-chat__head">
                  <strong>Rynn</strong>
                  <span>Your Compliance Intelligence Director</span>
                </div>
                <div className="landing-mock-chat__body">
                  {[
                    {
                      k: '1',
                      side: 'rynn',
                      text: 'Your medical card expires in 27 days. Here\'s what to do...',
                    },
                    { k: '2', side: 'user', text: 'What documents do I need for a DOT audit?' },
                    {
                      k: '3',
                      side: 'rynn',
                      text:
                        'For a DOT audit you\'ll need your Driver Qualification File, including your CDL, medical card, and MVR...',
                    },
                  ].map((m) => (
                    <motion.div
                      key={m.k}
                      className={`landing-mock-chat__bubble landing-mock-chat__bubble--${m.side}`}
                      variants={{
                        hidden: reduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 },
                        show: { opacity: 1, y: 0, transition: springTransition(reduceMotion) },
                      }}
                    >
                      {m.text}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>

      {/* Big statement */}
      <section className="landing-statement" aria-label="Statement">
        <div className="landing-statement__bg-lines" aria-hidden>
          {[32, 50, 68].map((top, i) => (
            <motion.div
              key={top}
              className="landing-statement__sweep"
              style={{ top: `${top}%`, width: '100%' }}
              initial={reduceMotion ? false : { x: '-100%', opacity: 0 }}
              whileInView={{ x: '0%', opacity: 0.25 }}
              viewport={viewAnim}
              transition={{ delay: reduceMotion ? 0 : 0.2 + i * 0.1, duration: reduceMotion ? 0 : 1.2, ease: 'easeOut' }}
            />
          ))}
        </div>
        <motion.h2
          className="landing-statement__title"
          initial={reduceMotion ? false : { opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewAnim}
          transition={springTransition(reduceMotion)}
        >
          <span className="landing-statement__title-line landing-statement__title-line--white">YOUR OPERATION.</span>
          <span className="landing-statement__title-line landing-statement__title-line--amber">YOUR INTELLIGENCE.</span>
        </motion.h2>
        <motion.div
          className="landing-statement__trails"
          aria-hidden
          initial={reduceMotion ? false : { opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={viewAnim}
          transition={{ delay: reduceMotion ? 0 : 0.35, duration: reduceMotion ? 0 : 0.6 }}
        >
          <motion.div
            className="lp-trail landing-statement__trail--1"
            initial={reduceMotion ? false : { scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={viewAnim}
            style={{ transformOrigin: 'left center' }}
            transition={{ delay: reduceMotion ? 0 : 0.4, duration: reduceMotion ? 0 : 0.7, ease: 'easeOut' }}
          />
          <motion.div
            className="lp-trail lp-trail--dim landing-statement__trail--2"
            initial={reduceMotion ? false : { scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={viewAnim}
            style={{ transformOrigin: 'left center' }}
            transition={{ delay: reduceMotion ? 0 : 0.5, duration: reduceMotion ? 0 : 0.7, ease: 'easeOut' }}
          />
          <motion.div
            className="lp-trail landing-statement__trail--3"
            initial={reduceMotion ? false : { scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={viewAnim}
            style={{ transformOrigin: 'left center' }}
            transition={{ delay: reduceMotion ? 0 : 0.6, duration: reduceMotion ? 0 : 0.7, ease: 'easeOut' }}
          />
        </motion.div>
      </section>

      {/* Pricing */}
      <section className="landing-pricing" aria-labelledby="lp-price-h">
        <div className="landing-pricing__inner">
          <div className="landing-pricing__head">
            <p className="landing-section-label">PRICING</p>
            <h2 id="lp-price-h" className="landing-pricing__title">
              Your plan. Your operation.
            </h2>
            <p className="landing-pricing__sub">Pick what fits now. Upgrade when your business demands it.</p>
          </div>
          <div className="landing-pricing__grid">
            <motion.article
              className="landing-price-card"
              initial={reduceMotion ? false : { opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewAnim}
              transition={{ ...springTransition(reduceMotion), delay: reduceMotion ? 0 : 0 }}
            >
              <h3 className="landing-price-card__name">Free</h3>
              <p className="landing-price-card__price">
                {FREE_PLAN_PRICE}
              </p>
              <p className="landing-price-card__desc">Get familiar with Sovrynn. No card required.</p>
              <div className="landing-price-card__divider" />
              <ul className="landing-price-card__features">
                <li>Compliance dashboard</li>
                <li>Document storage</li>
                <li>Vehicle tracking (1–2 trucks)</li>
                <li>Authority lookup</li>
                <li>Basic alerts</li>
              </ul>
              <Link to="/register" className="landing-price-card__cta-outline">
                Get Started Free
              </Link>
            </motion.article>

            <motion.article
              className="landing-price-card landing-price-card--featured"
              initial={reduceMotion ? false : { opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewAnim}
              transition={{ ...springTransition(reduceMotion), delay: reduceMotion ? 0 : 0.08 }}
            >
              <span className="landing-price-card__badge">MOST POPULAR</span>
              <h3 className="landing-price-card__name">Owner-Operator</h3>
              <p className="landing-price-card__price">
                {LANDING_OWNER_OPERATOR?.price ?? '$20'}{' '}
                <span className="landing-price-card__period">/mo</span>
              </p>
              <p className="landing-price-card__desc">
                You carry driver, dispatcher, safety, and compliance responsibilities at once. Sovrynn gives you the
                intelligence layer that keeps every part of your record clear and verifiable.
              </p>
              <div className="landing-price-card__divider" />
              <ul className="landing-price-card__features">
                <li>Compliance Intelligence Dashboard</li>
                <li>DRI Score™ — compliance standing scored in real time</li>
                <li>Compliance Passport™</li>
                <li>Vehicle Readiness Score™</li>
                <li>Document expiration alerts</li>
                <li>IFTA tracking</li>
                <li>Authority compliance monitoring</li>
                <li>Rynn AI compliance assistant</li>
              </ul>
              <Link to="/register" className="landing-price-card__cta-solid">
                Get Your Free DRI Score™
              </Link>
            </motion.article>

            <motion.article
              className="landing-price-card"
              initial={reduceMotion ? false : { opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewAnim}
              transition={{ ...springTransition(reduceMotion), delay: reduceMotion ? 0 : 0.16 }}
            >
              <h3 className="landing-price-card__name">Fleet / Pro</h3>
              <p className="landing-price-card__price">
                {LANDING_FLEET?.price ?? '$40'}{' '}
                <span className="landing-price-card__period">
                  /{LANDING_FLEET?.period ?? 'mo'}
                </span>
                {LANDING_FLEET?.priceAddon ? (
                  <span className="landing-price-card__addon">
                    {LANDING_FLEET.priceAddon}
                  </span>
                ) : null}
              </p>
              <p className="landing-price-card__desc">Everything in Owner-Operator, built for multi-driver operations.</p>
              <div className="landing-price-card__divider" />
              <ul className="landing-price-card__features">
                <li>Everything in Owner-Operator</li>
                <li>Up to 25 vehicles</li>
                <li>Multi-driver compliance intelligence</li>
                <li>Fleet DRI Score™</li>
                <li>Fleet Compliance Passport™</li>
                <li>Fleet analytics dashboard + reports</li>
                <li>Priority support</li>
              </ul>
              <Link to="/register" className="landing-price-card__cta-outline">
                Get Started
              </Link>
            </motion.article>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="landing-final" aria-labelledby="lp-final-h">
        <div className="landing-final__glow" aria-hidden />
        <div className="landing-final__inner">
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewAnim}
            transition={springTransition(reduceMotion)}
          >
            <div className="landing-final__wordmark">Sovrynn</div>
            <div className="landing-final__wordmark-trails" aria-hidden>
              <div className="lp-trail" style={{ width: 120 }} />
              <div className="lp-trail lp-trail--dim" style={{ width: 72 }} />
            </div>
          </motion.div>
          <motion.h2
            id="lp-final-h"
            className="landing-final__headline"
            initial={reduceMotion ? false : { opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewAnim}
            transition={{ ...springTransition(reduceMotion), delay: reduceMotion ? 0 : 0.06 }}
          >
            Start free.
          </motion.h2>
          <motion.p
            className="landing-final__sub"
            initial={reduceMotion ? false : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewAnim}
            transition={{ ...springTransition(reduceMotion), delay: reduceMotion ? 0 : 0.12 }}
          >
            Know your DRI Score™ in under 5 minutes.
          </motion.p>
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewAnim}
            transition={{ ...springTransition(reduceMotion), delay: reduceMotion ? 0 : 0.18 }}
          >
            <Link to="/register" className="landing-btn-primary landing-btn-primary--lg">
              Get Your Free DRI Score™
            </Link>
            <p className="landing-final__signin">
              Already have an account?{' '}
              <Link to="/login">Sign in →</Link>
            </p>
          </motion.div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer__grid">
          <div>
            <div className="landing-footer__wordmark">Sovrynn</div>
            <div className="landing-footer__wordmark-trails" aria-hidden>
              <div className="lp-trail" style={{ width: 40, height: 2 }} />
              <div className="lp-trail lp-trail--dim" style={{ width: 26, height: 2, marginTop: 4 }} />
            </div>
            <p className="landing-footer__tag">Compliance Intelligence for the independent commercial operator</p>
            <p className="landing-footer__co">A Tearratopia Holdings Company</p>
          </div>
          <div className="landing-footer__links">
            <Link to="/blog">Blog</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/login">Sign In</Link>
          </div>
        </div>
        <p className="landing-footer__copy">© 2026 Sovrynn LLC. All rights reserved.</p>
      </footer>
    </div>
  );
}
